'use client'

import { useState, type ReactElement } from 'react'

import { HorizontalCard } from '@/app/components/shared/cards/HorizontalCard'
import { VerticalCard } from '@/app/components/shared/cards/VerticalCard'
import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { buildProductCardClick } from '@/app/lib/analytics/buildCardClick'
import { withIndex } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import { useProductListTracking } from '@/app/lib/analytics/useProductListTracking'

import {
  ITEMS_PER_PAGE,
  LABEL_MORE,
  SIMILAR_ITEM_LIST_ID,
  SIMILAR_ITEM_LIST_NAME,
  SIMILAR_MODULE_NAME,
  SIMILAR_MODULE_POSITION,
  SIMILAR_MODULE_TYPE,
} from './constants'
import styles from './SimilarPerformance.module.scss'
import type { SimilarPerformanceListProps } from './types'

/**
 * Las dos rejillas de «También te puede interesar» (mobile/tablet y desktop,
 * alternadas por CSS) más el botón «Ver más» — la ÚNICA hoja cliente de la
 * sección: `SimilarPerformance.tsx` sigue siendo Server Component
 * (Constitución, Principio I).
 *
 * De partida solo se ven las 4 primeras tarjetas; cada pulsación de «Ver más»
 * revela otras 4 y el botón desaparece cuando ya no queda ninguna oculta. El
 * recorte se aplica en los dos anchos por igual, no solo en mobile: ambas
 * rejillas son de 2 columnas, así que 4 tarjetas son dos filas completas en
 * cualquier caso.
 *
 * Todas las tarjetas se renderizan SIEMPRE (las no reveladas con el atributo
 * nativo `hidden`), igual que en `ProductRail`: el HTML lo sigue generando el
 * servidor —indexable y sin volver a pedir datos al revelar— y `hidden` ya
 * saca sus descendientes del árbol de accesibilidad y del orden de tabulación
 * mientras están plegados. Sus imágenes son `lazy` (ver `Poster`), así que las
 * tarjetas ocultas tampoco descargan cartel hasta que se revelan.
 */
export function SimilarPerformanceList({ items, analyticsItems }: SimilarPerformanceListProps) {
  const [visibleCount, setVisibleCount] = useState<number>(ITEMS_PER_PAGE)

  const hiddenCount = Math.max(items.length - visibleCount, 0)
  const shownCount = Math.min(visibleCount, items.length)

  // Analítica (`Grid relacionados.csv`): `view_item_list` agrupado por
  // impresión + `select_item` al clicar — mismo hook que las PLPs
  // (`useProductListTracking`). Sin `analyticsItems` (longitud distinta o
  // ausente) no se instrumenta nada; el listado sigue funcionando igual.
  const analytics = useOptionalPageAnalytics()
  const { trackImpression, trackSelect } = useProductListTracking(
    {
      itemListId: SIMILAR_ITEM_LIST_ID,
      itemListName: SIMILAR_ITEM_LIST_NAME,
      moduleName: SIMILAR_MODULE_NAME,
      moduleType: SIMILAR_MODULE_TYPE,
      modulePosition: SIMILAR_MODULE_POSITION,
    },
    { trackViewItemList: analytics?.trackViewItemList, trackSelectItem: analytics?.trackSelectItem },
  )
  const hasAnalytics = analyticsItems?.length === items.length

  /**
   * Envuelve una card ya renderizada con el tracking de impresión/click.
   * Ambas rejillas (mobile/desktop) comparten la MISMA `key` de deduplicación
   * (`item.item_id`, en `useProductListTracking`): solo la instancia
   * realmente visible en cada breakpoint cruza el `IntersectionObserver` (la
   * otra está en `display: none` vía CSS), así que no hay doble impresión.
   */
  function renderTracked(index: number, card: ReactElement) {
    const base = analyticsItems?.[index]
    if (!hasAnalytics || !base) return card
    const item = withIndex(base, index)
    return (
      <TrackedProductCard
        index={index}
        onImpression={() => trackImpression(item.item_id, item)}
        onSelect={(href) => {
          trackSelect(item)
          // `click` de card (`Grid relacionados.csv` → "Clic en card de producto relacionado").
          analytics?.trackClick(buildProductCardClick(item, href))
        }}
      >
        {card}
      </TrackedProductCard>
    )
  }

  return (
    <>
      {/* Mobile/tablet — oculta desde desktop (ver `.mobileGrid`). */}
      <ul className={styles.mobileGrid}>
        {items.map(({ id, ...card }, index) => (
          <li key={id} hidden={index >= visibleCount}>
            {renderTracked(index, <VerticalCard {...card} showCta={false} />)}
          </li>
        ))}
      </ul>

      {/* Desktop — sustituye a `.mobileGrid` (ver `.desktopGrid`). */}
      <ul className={styles.desktopGrid}>
        {items.map(({ id, ...card }, index) => (
          <li key={id} hidden={index >= visibleCount}>
            {renderTracked(index, <HorizontalCard {...card} showCta={false} />)}
          </li>
        ))}
      </ul>

      {hiddenCount > 0 && (
        <div className={styles.moreWrap}>
          <button
            type="button"
            className={styles.moreBtn}
            onClick={() => setVisibleCount((count) => count + ITEMS_PER_PAGE)}
          >
            {LABEL_MORE}
          </button>
        </div>
      )}

      {/* El botón desaparece con la última tanda, así que el foco se pierde y
          nada avisaría del cambio a un lector de pantalla: esta región lo
          anuncia. Presente ya en el HTML inicial (no se anuncia al cargar,
          solo al actualizarse). */}
      <p role="status" className={styles.status}>
        {`Mostrando ${shownCount} de ${items.length} eventos`}
      </p>
    </>
  )
}
